/**
 * TreatX Document Context — Phase 2C
 * ====================================
 * REMOVED: sessionStorage for clinicDocuments, patientSignatures, notifications
 * REMOVED: hardcoded initialClinicDocuments and treatxGlobalDocuments inline
 *
 * Backend APIs used:
 *   GET/POST/PUT/DELETE /api/documents   — legal document CRUD
 *   POST /api/documents/:id/versions     — version upload (metadata-only)
 *   GET/POST /api/signatures             — patient signatures
 *
 * R6-IMPL-005 (2026-02-28): migrated all HTTP calls onto the shared
 * `api` instance. Requests now inherit:
 *   - JWT auth header (`Authorization: Bearer …`)
 *   - `X-Active-Workspace` header derived from the user's active
 *     workspace (R3-IMPL-001 → broadened to /documents + /signatures)
 *   - centralised request normalisation
 *
 * NOT in scope: multipart upload semantics (none exist in this file),
 * blob preview (MedicalFilesPage owns that under MEDIA-001), document
 * gating workflow (deferred to R6-IMPL-003).
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuthGate } from '../hooks/useAuthGate';
// TREATX-BATCH-BOOT-01 (residual sweep) — documents/signatures are
// clinic-side per-actor. Patients do not consume them; scope the
// fetch to prevent 403 noise on patient login.
const _DOC_ROLES = [
  'clinic_admin', 'provider_admin', 'clinic_staff', 'clinic_coordinator',
  'coordinator', 'sales_rep', 'admin', 'treatx_admin', 'super_admin', 'platform_admin',
];
import { api } from '../utils/api';

const DocumentContext = createContext();

export const useDocuments = () => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error('useDocuments must be used within DocumentProvider');
  }
  return context;
};

export const REQUIRED_STAGES = {
  PRE_INTAKE: { id: 'PRE_INTAKE', label: 'Before Intake', color: 'bg-red-100 text-red-700' },
  PRE_PAYMENT: { id: 'PRE_PAYMENT', label: 'Before Payment', color: 'bg-amber-100 text-amber-700' },
  PRE_TRAVEL: { id: 'PRE_TRAVEL', label: 'Before Travel', color: 'bg-blue-100 text-blue-700' },
  PRE_SURGERY: { id: 'PRE_SURGERY', label: 'Before Surgery', color: 'bg-purple-100 text-purple-700' },
  POST_SURGERY: { id: 'POST_SURGERY', label: 'After Surgery', color: 'bg-green-100 text-green-700' },
  OPTIONAL: { id: 'OPTIONAL', label: 'Optional', color: 'bg-slate-100 text-slate-700' },
};

const normalizeDoc = (d) => ({
  id: d.document_id,
  owner: d.owner,
  clinicId: d.clinic_id,
  title: d.title,
  description: d.description,
  category: d.category,
  requiredStage: d.required_stage,
  versions: (d.versions || []).map(v => ({
    version: v.version,
    uploadDate: v.upload_date,
    fileUrl: v.file_url,
    uploadedBy: v.uploaded_by,
    notes: v.notes,
  })),
  currentVersion: d.current_version,
  isRequired: d.is_required,
  requiresSignature: d.requires_signature,
  isBlocking: d.is_blocking,
  blockingMessage: d.blocking_message || '',
  createdAt: d.created_at,
});

const normalizeSig = (s) => ({
  id: s.signature_id,
  patientId: s.patient_id,
  documentId: s.document_id,
  clinicId: s.clinic_id,
  owner: s.owner,
  signatureValue: s.typed_name,
  agreedToTerms: s.agreed,
  signedAt: s.signed_at,
});

export const DocumentProvider = ({ children }) => {
  const [allDocuments, setAllDocuments] = useState([]);
  const [patientSignatures, setPatientSignatures] = useState([]);
  const [newDocumentNotifications, setNewDocumentNotifications] = useState([]);

  const treatxDocuments = allDocuments.filter(d => d.owner === 'TREATX');
  const clinicDocuments = allDocuments.filter(d => d.owner === 'CLINIC');

  const fetchDocuments = useCallback(async () => {
    try {
      const { data } = await api.get('/documents');
      setAllDocuments((data.documents || []).map(normalizeDoc));
    } catch (err) { console.error('[Documents] fetchDocuments error:', err); }
  }, []);

  const fetchSignatures = useCallback(async (patientId) => {
    try {
      const params = patientId ? { patient_id: patientId } : {};
      const { data } = await api.get('/signatures', { params });
      setPatientSignatures((data.signatures || []).map(normalizeSig));
    } catch (err) { console.error('[Documents] fetchSignatures error:', err); }
  }, []);

  // TREATX-BATCH-BOOT-01 — protected fetch gated on authReady. Docs
  // + signatures are per-actor; any authenticated user may read their
  // own via RBAC-scoped backend routes.
  const { authorized: _doc_authorized } = useAuthGate({ roles: _DOC_ROLES });
  useEffect(() => {
    if (!_doc_authorized) return;
    fetchDocuments();
    fetchSignatures();
  }, [_doc_authorized, fetchDocuments, fetchSignatures]);

  const getTreatxDocuments = useCallback(() => treatxDocuments, [treatxDocuments]);

  const getDocumentsForClinic = (clinicId) => clinicDocuments.filter(doc => doc.clinicId === clinicId);

  const getAllDocumentsForPatient = (patientId, clinicId) => {
    const clinic = clinicId ? getDocumentsForClinic(clinicId) : [];
    return { treatxDocuments, clinicDocuments: clinic };
  };

  const getPendingTreatxDocuments = (patientId) => {
    const signedDocIds = patientSignatures.filter(sig => sig.patientId === patientId && sig.owner === 'TREATX').map(sig => sig.documentId);
    return treatxDocuments.filter(doc => doc.requiresSignature && doc.isRequired && !signedDocIds.includes(doc.id));
  };

  const getPendingClinicDocuments = (patientId, clinicId) => {
    const docs = getDocumentsForClinic(clinicId);
    const signedDocIds = patientSignatures.filter(sig => sig.patientId === patientId && sig.clinicId === clinicId).map(sig => sig.documentId);
    return docs.filter(doc => doc.requiresSignature && doc.isRequired && !signedDocIds.includes(doc.id));
  };

  const getPendingDocuments = (patientId, clinicId) => ({
    treatxDocuments: getPendingTreatxDocuments(patientId),
    clinicDocuments: clinicId ? getPendingClinicDocuments(patientId, clinicId) : [],
  });

  const getSignedDocuments = (patientId) => {
    return patientSignatures.filter(sig => sig.patientId === patientId).map(sig => {
      const doc = allDocuments.find(d => d.id === sig.documentId);
      return doc ? { ...doc, signature: sig } : null;
    }).filter(Boolean);
  };

  const getOptionalDocuments = (patientId, clinicId) => {
    const docs = clinicId ? getDocumentsForClinic(clinicId) : [];
    return docs.filter(doc => !doc.isRequired || !doc.requiresSignature);
  };

  const isGdprSigned = useCallback((patientId) => {
    return patientSignatures.some(sig => sig.patientId === patientId && sig.documentId === 'treatx-doc-001');
  }, [patientSignatures]);

  const isPaymentPolicySigned = useCallback((patientId) => {
    return patientSignatures.some(sig => sig.patientId === patientId && sig.documentId === 'treatx-doc-002');
  }, [patientSignatures]);

  const getBlockingDocument = (patientId, stage) => {
    return getPendingTreatxDocuments(patientId).find(doc => doc.isBlocking && doc.requiredStage === stage);
  };

  // Add document via API
  const addDocument = async (clinicId, documentData) => {
    try {
      const { data } = await api.post('/documents', {
        owner: 'CLINIC',
        clinic_id: clinicId,
        title: documentData.title,
        description: documentData.description || '',
        category: documentData.category || 'legal',
        required_stage: documentData.requiredStage || 'OPTIONAL',
        is_required: documentData.isRequired || false,
        requires_signature: documentData.requiresSignature || false,
      });
      if (data.success) {
        await fetchDocuments();
        return normalizeDoc(data.document);
      }
    } catch (err) { console.error('[Documents] addDocument error:', err); }
    return null;
  };

  const uploadNewVersion = async (documentId, versionData) => {
    try {
      const { data } = await api.post(`/documents/${documentId}/versions`, {
        uploaded_by: versionData.uploadedBy || 'Admin',
        notes: versionData.notes || '',
      });
      if (data.success) await fetchDocuments();
    } catch (err) { console.error('[Documents] uploadNewVersion error:', err); }
  };

  const updateDocument = async (documentId, updates) => {
    try {
      await api.put(`/documents/${documentId}`, {
        title: updates.title,
        description: updates.description,
        category: updates.category,
        required_stage: updates.requiredStage,
        is_required: updates.isRequired,
        requires_signature: updates.requiresSignature,
      });
      await fetchDocuments();
    } catch (err) { console.error('[Documents] updateDocument error:', err); }
  };

  const deleteDocument = async (documentId) => {
    try {
      await api.delete(`/documents/${documentId}`);
      await fetchDocuments();
    } catch (err) { console.error('[Documents] deleteDocument error:', err); }
  };

  // Sign document via API
  const signDocument = async (patientId, documentId, clinicId, signatureData) => {
    const isTreatxDoc = documentId.startsWith('treatx-');
    try {
      const { data } = await api.post('/signatures', {
        patient_id: patientId,
        document_id: documentId,
        clinic_id: isTreatxDoc ? null : clinicId,
        owner: isTreatxDoc ? 'TREATX' : 'CLINIC',
        typed_name: signatureData.typedName,
        agreed: signatureData.agreed,
      });
      if (data.success) {
        await fetchSignatures(patientId);
        return normalizeSig(data.signature);
      }
    } catch (err) { console.error('[Documents] signDocument error:', err); }
    return null;
  };

  const hasSignedAllForStage = (patientId, clinicId, stage) => {
    const tDocs = treatxDocuments.filter(d => d.requiredStage === stage && d.isRequired);
    const cDocs = clinicId ? getDocumentsForClinic(clinicId).filter(d => d.requiredStage === stage && d.isRequired) : [];
    return [...tDocs, ...cDocs].every(doc => patientSignatures.some(sig => sig.patientId === patientId && sig.documentId === doc.id));
  };

  const getDocumentById = (documentId) => allDocuments.find(doc => doc.id === documentId);

  const requiresUpdate = (patientId, documentId) => {
    const doc = getDocumentById(documentId);
    if (!doc) return false;
    const signature = patientSignatures.find(sig => sig.patientId === patientId && sig.documentId === documentId);
    if (!signature) return false;
    const signedVersion = doc.versions.find(v => new Date(v.uploadDate) <= new Date(signature.signedAt));
    return signedVersion && signedVersion.version !== doc.currentVersion;
  };

  const getNewDocumentCount = (patientId, clinicId) => newDocumentNotifications.filter(n => n.clinicId === clinicId).length;

  const clearNewNotification = (documentId) => {
    setNewDocumentNotifications(prev => prev.filter(n => n.documentId !== documentId));
  };

  const value = {
    clinicDocuments, treatxDocuments, patientSignatures,
    getTreatxDocuments, getDocumentsForClinic, getAllDocumentsForPatient,
    getPendingTreatxDocuments, getPendingClinicDocuments, getPendingDocuments,
    getSignedDocuments, getOptionalDocuments,
    isGdprSigned, isPaymentPolicySigned, getBlockingDocument,
    addDocument, uploadNewVersion, updateDocument, deleteDocument, signDocument,
    hasSignedAllForStage, getDocumentById, requiresUpdate,
    getNewDocumentCount, clearNewNotification,
    REQUIRED_STAGES,
  };

  return (
    <DocumentContext.Provider value={value}>
      {children}
    </DocumentContext.Provider>
  );
};

export default DocumentContext;
